// Composer voice-recording bar — replaces the input pill while the
// user is capturing / previewing a voice message. Pixel-pinned to
// Figma:
//
//   - `3125:3929` (recording): trash | red-dot + timer + live blue
//     waveform | pause | indigo send
//   - `3139:301`  (paused):     trash | play  + timer + gray waveform
//     with blue progress | mic | indigo send
//   - `3139:435`  (playing):    trash | pause + timer + blue-played /
//     gray-unplayed waveform with playhead dot | mic | indigo send

import React, { useMemo, useState } from 'react';
import {
  type LayoutChangeEvent,
  Pressable,
  Text,
  View,
} from 'react-native';
import { Mic, Pause, Play, Send, Trash2 } from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';

export type VoiceBarPhase = 'recording' | 'paused' | 'playing';

export interface ComposerVoiceBarProps {
  readonly phase: VoiceBarPhase;
  readonly elapsedMs: number;
  readonly amplitudes: ReadonlyArray<number>;
  readonly progress: number;
  readonly onCancel: () => void;
  readonly onTogglePlayPause: () => void;
  readonly onRestartRecording: () => void;
  readonly onSend: () => void;
  readonly testID?: string | undefined;
}

function formatTimer(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

const BAR_WIDTH = 2;
const BAR_GAP = 5;
const BAR_HEIGHT_MAX = 18;
const BAR_HEIGHT_MIN = 6;
const BAR_STRIDE = BAR_WIDTH + BAR_GAP;

export const ComposerVoiceBar = React.memo<ComposerVoiceBarProps>(
  ({
    phase,
    elapsedMs,
    amplitudes,
    progress,
    onCancel,
    onTogglePlayPause,
    onRestartRecording,
    onSend,
    testID,
  }) => {
    const colors = tokens.color.semantic.dark; // bar lives on a dark surface

    const activeBar = '#537EFF';
    const rose = '#FF5353';

    const [waveformWidth, setWaveformWidth] = useState(0);
    const onWaveformLayout = (event: LayoutChangeEvent): void => {
      setWaveformWidth(event.nativeEvent.layout.width);
    };

    const maxBars = Math.max(1, Math.floor(waveformWidth / BAR_STRIDE));
    const visibleAmplitudes = useMemo<ReadonlyArray<number>>(() => {
      if (amplitudes.length <= maxBars) return amplitudes;
      return amplitudes.slice(amplitudes.length - maxBars);
    }, [amplitudes, maxBars]);

    const playedThreshold =
      phase === 'recording'
        ? visibleAmplitudes.length
        : Math.max(
            0,
            Math.min(
              visibleAmplitudes.length,
              Math.round(visibleAmplitudes.length * progress),
            ),
          );

    const leftIcon = (() => {
      switch (phase) {
        case 'recording':
          // 10x10 red dot, rounded full. Inline backgroundColor
          // because `#FF5353` isn't a token.
          return (
            <View
              className="w-[10px] h-[10px] rounded-full"
              style={{ backgroundColor: rose }}
            />
          );
        case 'paused':
          return <Play size={18} strokeWidth={2} color={activeBar} fill={activeBar} />;
        case 'playing':
          return <Pause size={18} strokeWidth={2} color={activeBar} fill={activeBar} />;
      }
    })();

    const SecondaryIcon = phase === 'recording' ? Pause : Mic;
    const secondaryHandler =
      phase === 'recording' ? onTogglePlayPause : onRestartRecording;
    const secondaryLabel =
      phase === 'recording' ? 'Pause recording' : 'Restart recording';

    return (
      <View
        className="flex flex-row items-center gap-sm flex-1 min-w-0"
        testID={testID}
      >
        {/*
          Trash — outside the pill, leading edge. Rose/red outline per
          Figma `3125:3929`. 30×30 hit area matches the composer's
          other icon buttons.
        */}
        <Pressable
          onPress={onCancel}
          accessibilityRole="button"
          accessibilityLabel="Discard recording"
          className="w-[38px] h-[38px] rounded-pill items-center justify-center"
          testID="composer-voice-cancel"
        >
          <Trash2 size={24} strokeWidth={2} color={rose} />
        </Pressable>

        {/*
          Recording pill — 40 tall, pill radius. Hosts (left→right):
          left icon (dot/play/pause), timer (MM:SS), live waveform.
        */}
        <Pressable
          onPress={phase === 'recording' ? undefined : onTogglePlayPause}
          accessibilityRole={phase === 'recording' ? 'none' : 'button'}
          accessibilityLabel={
            phase === 'paused'
              ? 'Play recording'
              : phase === 'playing'
                ? 'Pause playback'
                : 'Recording'
          }
          className="
            flex-1 min-h-10 rounded-pill flex flex-row items-center
            px-[14px] gap-[10px] min-w-0 overflow-hidden
            bg-light-surface-secondary dark:bg-[#222429]
          "
        >
          <View className="w-[18px] h-[18px] items-center justify-center">
            {leftIcon}
          </View>
          <Text
            className="
              font-sans text-[13px] leading-4 font-semibold min-w-[32px]
              text-light-text-secondary dark:text-dark-text-secondary
            "
            style={{ fontVariant: ['tabular-nums'] }}
            accessibilityLabel={`Duration ${formatTimer(elapsedMs)}`}
          >
            {formatTimer(elapsedMs)}
          </Text>
          {/*
            Live waveform — bars fill the available track from the
            right so the latest sample is always flush against the
            trailing edge (Figma `3125:3929`).
          */}
          <View
            className="
              flex-1 flex flex-row items-center justify-end overflow-hidden min-w-0
            "
            style={{ height: BAR_HEIGHT_MAX, gap: BAR_GAP }}
            onLayout={onWaveformLayout}
          >
            {visibleAmplitudes.map((amp, idx) => {
              const clamped = Math.max(0, Math.min(1, amp));
              const height = BAR_HEIGHT_MIN + clamped * (BAR_HEIGHT_MAX - BAR_HEIGHT_MIN);
              const played = idx < playedThreshold;
              return (
                <View
                  // biome-ignore lint/suspicious/noArrayIndexKey: bar identity is positional
                  key={idx}
                  className="rounded-[2px]"
                  // Width + amplitude-driven height + color stay
                  // inline (per-frame values, not Tailwind-friendly).
                  style={{
                    width: BAR_WIDTH,
                    height,
                    backgroundColor: played
                      ? activeBar
                      : colors.border.primary,
                  }}
                />
              );
            })}
          </View>
        </Pressable>

        {/*
          Secondary action — pause (while recording) or mic (while
          paused/playing). Outside the pill, before the send button.
        */}
        <Pressable
          onPress={secondaryHandler}
          accessibilityRole="button"
          accessibilityLabel={secondaryLabel}
          className="w-[38px] h-[38px] rounded-pill items-center justify-center"
          testID="composer-voice-secondary"
        >
          <SecondaryIcon
            size={24}
            strokeWidth={2}
            color={colors.text.secondary}
          />
        </Pressable>

        {/* Indigo send — Figma `2804:26711` 38×38 pill. */}
        <Pressable
          onPress={onSend}
          accessibilityRole="button"
          accessibilityLabel="Send voice message"
          className="
            w-[38px] h-[38px] rounded-pill items-center justify-center
            bg-light-accent-indigo dark:bg-dark-accent-indigo
          "
          testID="composer-voice-send"
        >
          <Send size={20} strokeWidth={2} color="#FFFFFF" />
        </Pressable>
      </View>
    );
  },
);

ComposerVoiceBar.displayName = 'ComposerVoiceBar';
